import { PencilIcon } from "@heroicons/react/16/solid";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { DataTableFootnote } from "../../Models";
import { updateFootnote } from "../../context/DataTableReducer";
import useDataTableContext from "../../context/useDataTableContext";
import { getUsedFootnotes } from "../../helpers";
import EditFootnoteModal from "./Components/EditFootnoteModal/EditFootnoteModal";

const FootnotesContainer = () => {
    const { state, dispatch } = useDataTableContext();
    const { readOnly, rows, footnotes } = state;

    const [usedFootnotes, setUsedFootnotes] = useState<Array<DataTableFootnote>>([]);
    const [editFootnote, setEditFootnote] = useState<DataTableFootnote | null>();

    useEffect(() => {
        setUsedFootnotes(getUsedFootnotes(rows, footnotes, readOnly));
    }, [rows, footnotes, readOnly]);

    const onCancel = () => setEditFootnote(null);

    const onSubmit = (value: string) => {
        dispatch(updateFootnote(editFootnote!.id, value));
        setEditFootnote(null);
    };

    if (readOnly && usedFootnotes.length === 0) {
        return null;
    }

    if (readOnly && usedFootnotes.length > 0) {
        return (
            <div className="mt-5">
                {usedFootnotes.map((footnote, index) => (
                    <span key={footnote.id} className="block font-medium">
                        <span className="text-primary-500 font-bold">{index + 1})</span> {footnote.value}
                    </span>
                ))}
            </div>
        );
    }

    return (
        <>
            {usedFootnotes.map((footnote, index) => (
                <Box key={footnote.id} sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="body2">
                        <sup>{index + 1})</sup> {footnote.value}
                    </Typography>
                    {!readOnly &&
                        (["optional", "bimids", "epset"].findIndex((x) => x === footnote.notetype) > -1 ? (
                            <Box sx={{ display: "inline-flex" }}>
                                <Tooltip
                                    title={`This footnote was automatically added and cannot be edited here. To edit this footnote modify it in the site settings (key: ${footnote.notetype})`}
                                >
                                    <Box sx={{ display: "inline-block" }} component="span">
                                        <IconButton
                                            size="small"
                                            tabIndex={-1}
                                            onClick={() => setEditFootnote(footnote)}
                                            sx={{ mr: 1, ml: "auto" }}
                                            disableRipple
                                            disabled
                                        >
                                            <PencilIcon className="w-4 h-4" />
                                        </IconButton>
                                    </Box>
                                </Tooltip>
                            </Box>
                        ) : (
                            <Box sx={{ display: "inline-block" }} component="span">
                                <IconButton
                                    size="small"
                                    tabIndex={-1}
                                    onClick={() => setEditFootnote(footnote)}
                                    sx={{ mr: 1, ml: "auto" }}
                                    disableRipple
                                >
                                    <PencilIcon className="w-4 h-4" />
                                </IconButton>
                            </Box>
                        ))}
                </Box>
            ))}

            {!readOnly && editFootnote && (
                <EditFootnoteModal footnote={editFootnote} onClose={onCancel} onSubmit={onSubmit} />
            )}
        </>
    );
};

export default FootnotesContainer;
